// ════════════════════════════════════════════════════════════════════
// TERROR — H9's FACE · THE TITLES RAIL
// ────────────────────────────────────────────────────────────────────
// Every achievement family, its arc, and the sentence at the end of it.
// Drops into the Deeds & Renown gallery under the cross-game rail.
//
//   <TitlesRail uid={uid} map={achievementMap} />
//
// ── WHY IT LIVES IN THE GALLERY AND NOT IN A WARDROBE ────────────────
// Terror has no wardrobe screen. It has a cosmetic SCHEMA (six slots,
// grantCosmetic, equipCosmetic) and exactly one surface that reads a
// title back out — TheMark. Until a wardrobe exists, the honest home for
// "choose what your nameplate says" is the wall where you can see WHY
// you own each one. When the wardrobe lands, this component moves; it
// takes props and owns no routing, so moving it is an import.
//
// ── WHAT IT DOES NOT DO ──────────────────────────────────────────────
// It never mints. useTitleSync owns the write; this only equips what is
// already owned, and an equip that fails says so instead of pretending.
// ════════════════════════════════════════════════════════════════════

import React, { useMemo, useState, useEffect } from "react";
import { GM } from "./theme";
import { useLang } from "./lang"; // M9
import { titleGallery, cosmeticIdFor, TITLE_TIER_META } from "./titles";
import { equipCosmetic, unequipSlot, getEquipped } from "./progression";

export default function TitlesRail({ uid, map }) {
  const { t } = useLang();
  const rows = useMemo(() => titleGallery(map || {}), [map]);
  const [equipped, setEquipped] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(true);

  // Read the worn title once on mount. Deliberately a read and not a
  // listener: nothing else in the app changes this field, and a
  // listener here would be a second subscription for a value that only
  // this component writes.
  useEffect(() => {
    if (!uid) return;
    let dead = false;
    getEquipped(uid)
      .then((e) => { if (!dead) setEquipped(e?.title || null); })
      .catch(() => {});
    return () => { dead = true; };
  }, [uid]);

  const earned = rows.filter((r) => r.complete);
  const locked = rows.filter((r) => !r.complete);

  const wear = async (row) => {
    if (!uid || busy) return;
    setErr(null);
    setBusy(row.id);
    const id = cosmeticIdFor(row.id);
    try {
      if (equipped === id) {
        await unequipSlot(uid, "title");
        setEquipped(null);
      } else {
        const res = await equipCosmetic(uid, "title", id);
        if (res?.success) setEquipped(id);
        // "Not unlocked" here means the mint has not landed yet — the
        // sync is idempotent and the next achievement fire catches it.
        // Say that, rather than showing a title that silently does not
        // stick.
        else setErr(res?.reason || "Could not equip.");
      }
    } catch (e) {
      setErr(e?.message || "Could not equip.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section style={{ marginBottom: 30 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          all: "unset", cursor: "pointer", display: "flex", width: "100%",
          alignItems: "baseline", gap: 10, marginBottom: 10,
          borderBottom: `1px solid ${GM.pageBorder}`, paddingBottom: 6,
        }}
      >
        <span style={{ fontFamily: GM.fontD, fontSize: 19, color: GM.inkGoldBright }}>
          ❧ {t("titles.header", "Titles")}
        </span>
        <span style={{ fontSize: 11, color: GM.vellumDim }}>
          {t("titles.progress", "{have} of {need} families closed", { have: earned.length, need: rows.length })}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: GM.vellumFaint }}>
          {open ? "▾" : "▸"}
        </span>
      </button>

      {open && (
        <>
          <div style={{ fontSize: 12, color: GM.vellumDim, marginBottom: 12, fontFamily: GM.fontBody }}>
            {t("titles.blurb", "Every family of deeds ends in a sentence. Close the family, wear the sentence — it follows you into every game in the Empire.")}
          </div>

          {err && (
            <div style={{
              fontSize: 11, color: GM.inkRed, marginBottom: 10,
              border: `1px solid ${GM.inkRed}`, borderRadius: 4, padding: "5px 8px",
            }}>
              {err}
            </div>
          )}

          <div style={{ display: "grid", gap: 8 }}>
            {[...earned, ...locked].map((r) => {
              const id = cosmeticIdFor(r.id);
              const worn = equipped === id;
              const tm = TITLE_TIER_META[r.tier];
              return (
                <div
                  key={r.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: `1px solid ${worn ? tm.color : GM.pageBorder}`,
                    background: r.complete ? GM.pageMid : GM.pageWarm,
                    opacity: r.complete ? 1 : 0.55,
                    boxShadow: worn ? `0 0 0 1px ${tm.color}55, 0 0 14px ${tm.color}22` : "none",
                    transition: "border-color 200ms ease, box-shadow 200ms ease",
                  }}
                >
                  {/* tier pip — the whole ladder in four pixels */}
                  <div
                    title={`${tm.label} · ${tm.blurb}`}
                    style={{
                      width: 8, height: 8, borderRadius: "50%", flex: "0 0 auto",
                      background: r.complete ? tm.color : GM.silverDim,
                      boxShadow: r.complete ? `0 0 8px ${tm.color}88` : "none",
                    }}
                  />

                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{
                      fontFamily: GM.font, fontSize: 14,
                      color: r.complete ? GM.vellum : GM.vellumFaint,
                      fontStyle: r.masked ? "italic" : "normal",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {r.text}
                    </div>
                    <div style={{
                      fontSize: 11, color: GM.vellumDim, marginTop: 2,
                      fontFamily: GM.fontBody,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {r.name} · {r.blurb}
                    </div>
                  </div>

                  {/* progress, only while it means something */}
                  {!r.complete && (
                    <div style={{
                      fontSize: 11, color: GM.vellumFaint, fontFamily: GM.fontUI,
                      flex: "0 0 auto",
                    }}>
                      {r.have}/{r.need}
                    </div>
                  )}

                  {r.complete && (
                    <button
                      onClick={() => wear(r)}
                      disabled={busy === r.id}
                      style={{
                        all: "unset", cursor: busy === r.id ? "wait" : "pointer",
                        flex: "0 0 auto",
                        fontFamily: GM.fontUI, fontSize: 10, letterSpacing: "0.08em",
                        padding: "5px 10px", borderRadius: 4,
                        border: `1px solid ${worn ? tm.color : GM.pageBorder}`,
                        color: worn ? tm.color : GM.vellumDim,
                        background: worn ? `${tm.color}14` : "transparent",
                      }}
                    >
                      {busy === r.id ? "…" : worn ? t("titles.worn", "WORN") : t("titles.wear", "WEAR")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
